import { useEffect, useState } from "react";
import { MdAgriculture } from "react-icons/md";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioCultura } from "../../sync/repositories/agricultura";

const AJUDA = {
  titulo: "Cadastro de Culturas",
  descricao:
    "Culturas são as espécies plantadas na fazenda (ex.: Soja, Milho, Algodão). Esse cadastro é a base para os Plantios — ao criar um plantio, você escolhe a cultura que está sendo cultivada naquele talhão e safra.",
  secoes: [
    {
      titulo: "Dados da Cultura",
      campos: [
        {
          nome: "Nome da Cultura",
          obrigatorio: true,
          explicacao: "Nome pelo qual a cultura será identificada em toda a fazenda (ex.: 'Soja', 'Milho Safrinha'). Aparece nas listas de seleção de Plantios.",
        },
        {
          nome: "Ciclo (dias)",
          obrigatorio: false,
          explicacao: "Duração média, em dias, entre o plantio e a colheita dessa cultura. Serve como referência para planejar a safra, mas não bloqueia nem altera datas automaticamente.",
        },
        {
          nome: "Descrição",
          obrigatorio: false,
          explicacao: "Informações complementares sobre a cultura, como variedade típica, exigências de solo ou observações gerais de manejo.",
        },
      ],
    },
  ],
  dicas: [
    "Uma cultura com plantios vinculados pode não ser excluída — verifique se não há registros vinculados antes de tentar remover.",
  ],
};

export default function GestaoCulturas() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [culturas, setCulturas] = useState([]);
  const [culturasFiltradas, setCulturasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    ciclo_dias: "",
  });

  useEffect(() => {
    carregarCulturas();
  }, []);

  const carregarCulturas = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCultura.listar();
      
      const dados = response.data.results || response.data;
      setCulturas(dados);
      setCulturasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar culturas:", error);
      alert("Erro ao carregar lista de culturas");
    } finally {
      setCarregandoLista(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      alert("Preencha o nome da cultura.");
      return;
    }
    
    setLoading(true);

    try {
      if (editando) {
        // Editar cultura existente
        const response = await repositorioCultura.atualizar(editandoId, formData);
        if (response.status === 200) {
          alert("Cultura atualizada com sucesso!");
          handleCancel();
          carregarCulturas();
        }
      } else {
        // Cadastrar nova cultura
        const response = await repositorioCultura.criar(formData);
        if (response.status === 201 || response.status === 200) {
          alert("Cultura cadastrada com sucesso!");
          handleCancel();
          carregarCulturas();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (cultura) => {
    setFormData({
      nome: cultura.nome || "",
      descricao: cultura.descricao || "",
      ciclo_dias: cultura.ciclo_dias || "",
    });
    setEditando(true);
    setEditandoId(cultura.id);
    setMostrarFormulario(true);
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaCultura = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      descricao: "",
      ciclo_dias: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta cultura? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCultura.excluir(id);
      alert("Cultura excluída com sucesso!");
      carregarCulturas();
      
      // Se estava editando esta cultura, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir cultura. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...culturas];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.id?.toString().includes(buscaLower) ||
        c.descricao?.toLowerCase().includes(buscaLower)
      );
    }
    
    setCulturasFiltradas(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setCulturasFiltradas(culturas);
  };

  // Formatar ciclo para exibição
  const formatarCiclo = (dias) => {
    if (!dias) return "—";
    return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Culturas <MdAgriculture size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaCultura}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Cultura
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição - Aparece apenas quando necessário */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Cultura" : "Cadastrar Nova Cultura"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome da Cultura *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Digite o nome da cultura..."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Ciclo</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="ciclo_dias"
                        value={formData.ciclo_dias}
                        onChange={handleChange}
                        className="w-28 p-2 outline-none rounded-l focus:ring-2 focus:ring-green-300"
                        step="1"
                        min="0"
                      />
                      <div className="h-6 w-px bg-green-500" />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">dias</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded w-96 h-24 focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre a cultura..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Código, Nome ou Descrição</label>
                  <input
                    type="text"
                    placeholder="Digite o código, nome ou descrição da cultura..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Culturas */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {culturasFiltradas.length} cultura(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ciclo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {culturasFiltradas.map((cultura) => (
                        <tr key={cultura.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{cultura.id}</td>
                          <td className="p-3 text-sm font-medium">{cultura.nome}</td>
                          <td className="p-3 text-sm">{formatarCiclo(cultura.ciclo_dias)}</td>
                          <td className="p-3 text-sm max-w-md truncate">{cultura.descricao || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(cultura)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(cultura.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      ))}
                      
                      {culturasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhuma cultura encontrada com os filtros selecionados."
                              : "Nenhuma cultura cadastrada."}
                           </td>
                         </tr>
                      )}
                    </tbody>
                   </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}